import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Layers3,
  Link2,
  Plus,
  RefreshCw,
  Sparkles,
  Target,
  Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const nivelLabels = {
  trainee: 'Trainee',
  junior: 'Júnior',
  pleno: 'Pleno',
  senior: 'Sênior',
  coordenacao: 'Coordenação',
  gerencia: 'Gerência',
  diretoria: 'Diretoria'
};

const formatNivel = (nivel) => nivelLabels[nivel] || nivel || 'Não definido';

const formatDateTime = (value) => {
  if (!value) return 'Sem data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data';
  return date.toLocaleString('pt-BR');
};

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const DCFDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalCargos: 0,
      cargosComDCF: 0,
      dcfsPendentes: 0,
      funcionariosSemDCF: 0,
      taxaCobertura: 0
    },
    cargos: [],
    competencias: [],
    vinculacoes: [],
    insights: {
      totalCompetencias: 0,
      vinculacoesAtivas: 0,
      cargosSemVinculacao: 0,
      cargosSemCompetencias: 0,
      competenciasOciosas: 0,
      totalLacunas: 0,
      topCompetencias: [],
      departamentos: [],
      cargosRecentes: [],
      topCargos: []
    }
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    const requests = await Promise.allSettled([
      axios.get(`${API}/api/dcf/stats`, authConfig()),
      axios.get(`${API}/api/dcf/cargos`, authConfig()),
      axios.get(`${API}/api/dcf/competencias`, authConfig()),
      axios.get(`${API}/api/dcf/vinculacoes`, authConfig())
    ]);

    const stats = requests[0].status === 'fulfilled' ? requests[0].value.data || {} : {};
    const cargos = requests[1].status === 'fulfilled' ? requests[1].value.data || [] : [];
    const competencias = requests[2].status === 'fulfilled' ? requests[2].value.data || [] : [];
    const vinculacoes = requests[3].status === 'fulfilled' ? requests[3].value.data || [] : [];

    if (requests.every((result) => result.status === 'rejected')) {
      setError('Não foi possível carregar o panorama do DCF.');
    }

    const vinculacoesAtivas = vinculacoes.filter((item) => item.ativa !== false);
    const vinculacoesByCargo = vinculacoesAtivas.reduce((acc, item) => {
      if (!item.dcf_id) return acc;
      acc[item.dcf_id] = (acc[item.dcf_id] || 0) + 1;
      return acc;
    }, {});

    const competenciasMap = competencias.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});

    const usedCompetencias = new Set();
    const cargosEnriquecidos = cargos.map((cargo) => {
      const competenciaIds = [
        ...(cargo.competencias_tecnicas || []),
        ...(cargo.competencias_comportamentais || []),
        ...(cargo.competencias_gerenciais || [])
      ].filter(Boolean);

      competenciaIds.forEach((id) => usedCompetencias.add(id));

      const vinculados = Number(cargo.funcionarios_vinculados || vinculacoesByCargo[cargo.id] || 0);
      return {
        ...cargo,
        competencias_total: competenciaIds.length,
        vinculados
      };
    });

    const totalCargos = stats.totalCargos || cargosEnriquecidos.length;
    const cargosAtivos = stats.cargosComDCF || cargosEnriquecidos.filter((cargo) => cargo.status === 'ativo').length;
    const cargosSemVinculacao = cargosEnriquecidos.filter((cargo) => cargo.vinculados === 0).length;
    const cargosSemCompetencias = cargosEnriquecidos.filter((cargo) => cargo.competencias_total === 0).length;
    const competenciasOciosas = competencias.filter((comp) => !usedCompetencias.has(comp.id)).length;
    const totalCompetencias = competencias.length;
    const totalLacunas = cargosSemVinculacao + cargosSemCompetencias + competenciasOciosas + (stats.funcionariosSemDCF || 0);

    const topCompetencias = (stats.competenciasMaisDemandadas || [])
      .map((item) => ({
        nome: item.nome || competenciasMap[item.id]?.nome || 'Competência',
        quantidade: item.quantidade || 0
      }))
      .filter((item) => item.quantidade > 0)
      .slice(0, 6);

    const topCompetenciasFallback = Object.entries(
      cargosEnriquecidos.reduce((acc, cargo) => {
        [
          ...(cargo.competencias_tecnicas || []),
          ...(cargo.competencias_comportamentais || []),
          ...(cargo.competencias_gerenciais || [])
        ].forEach((id) => {
          if (!id) return;
          acc[id] = (acc[id] || 0) + 1;
        });
        return acc;
      }, {})
    )
      .map(([id, quantidade]) => ({
        nome: competenciasMap[id]?.nome || id,
        quantidade
      }))
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 6);

    const departamentos = Object.entries(
      cargosEnriquecidos.reduce((acc, cargo) => {
        const key = cargo.departamento || 'Não informado';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    )
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 6);

    const cargosRecentes = [...cargosEnriquecidos]
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
      .slice(0, 5);

    const topCargos = [...cargosEnriquecidos]
      .sort((a, b) => b.vinculados - a.vinculados)
      .slice(0, 5);

    setDashboardData({
      stats: {
        totalCargos,
        cargosComDCF: cargosAtivos,
        dcfsPendentes: stats.dcfsPendentes || cargosEnriquecidos.filter((cargo) => cargo.status && cargo.status !== 'ativo').length,
        funcionariosSemDCF: stats.funcionariosSemDCF || 0,
        taxaCobertura: stats.taxaCobertura || (totalCargos > 0 ? Number(((cargosAtivos / totalCargos) * 100).toFixed(1)) : 0)
      },
      cargos: cargosEnriquecidos,
      competencias,
      vinculacoes: vinculacoesAtivas,
      insights: {
        totalCompetencias,
        vinculacoesAtivas: vinculacoesAtivas.length,
        cargosSemVinculacao,
        cargosSemCompetencias,
        competenciasOciosas,
        totalLacunas,
        topCompetencias: topCompetencias.length > 0 ? topCompetencias : topCompetenciasFallback,
        departamentos,
        cargosRecentes,
        topCargos
      }
    });

    setLoading(false);
  };

  const quickActions = useMemo(() => ([
    {
      title: 'Novo cargo',
      description: 'Abrir o formulário de DCF já em modo de criação.',
      icon: Plus,
      className: 'bg-slate-900 text-white',
      action: () => navigate('/dcf/cargos?new=1')
    },
    {
      title: 'Nova competência',
      description: 'Cadastrar competência técnica, comportamental ou gerencial.',
      icon: Target,
      className: 'bg-emerald-600 text-white',
      action: () => navigate('/dcf/competencias?new=1')
    },
    {
      title: 'Vincular colaboradores',
      description: 'Resolver pendências de aderência entre cargo e colaborador.',
      icon: Link2,
      className: 'bg-amber-500 text-slate-950',
      action: () => navigate('/dcf/vinculacoes')
    },
    {
      title: 'Gerar template',
      description: 'Usar IA para acelerar a criação de uma nova estrutura.',
      icon: Sparkles,
      className: 'bg-white text-slate-900 border border-slate-200',
      action: () => navigate('/dcf/templates')
    }
  ]), [navigate]);

  const priorityItems = useMemo(() => ([
    {
      title: 'Colaboradores sem DCF',
      value: dashboardData.stats.funcionariosSemDCF,
      description: 'Funcionários ainda sem vínculo formal com um cargo DCF.',
      tone: 'border-rose-200 bg-rose-50 text-rose-900',
      actionLabel: 'Ir para vínculos',
      action: () => navigate('/dcf/vinculacoes')
    },
    {
      title: 'Cargos sem colaborador',
      value: dashboardData.insights.cargosSemVinculacao,
      description: 'Estruturas criadas no DCF que ainda não possuem ninguém vinculado.',
      tone: 'border-amber-200 bg-amber-50 text-amber-900',
      actionLabel: 'Revisar cargos',
      action: () => navigate('/dcf/cargos')
    },
    {
      title: 'Cargos sem competências',
      value: dashboardData.insights.cargosSemCompetencias,
      description: 'Descrições cadastradas, mas sem mapa mínimo de competências.',
      tone: 'border-sky-200 bg-sky-50 text-sky-900',
      actionLabel: 'Completar DCFs',
      action: () => navigate('/dcf/cargos')
    },
    {
      title: 'Competências ociosas',
      value: dashboardData.insights.competenciasOciosas,
      description: 'Itens cadastrados no banco que ainda não são usados em nenhum cargo.',
      tone: 'border-violet-200 bg-violet-50 text-violet-900',
      actionLabel: 'Organizar banco',
      action: () => navigate('/dcf/competencias')
    }
  ]), [dashboardData, navigate]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-5">
          <div className="h-28 rounded-3xl bg-slate-200" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-36 rounded-2xl bg-slate-200" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <div className="h-96 rounded-2xl bg-slate-200 xl:col-span-2" />
            <div className="h-96 rounded-2xl bg-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  const maxDepartamento = Math.max(...dashboardData.insights.departamentos.map((item) => item.quantidade), 1);

  return (
    <div className="p-6 space-y-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-900 text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1.5fr_1fr] lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-emerald-100">
              <ClipboardCheck className="h-3.5 w-3.5" />
              DCF Workspace
            </div>

            <div>
              <h1 className="max-w-3xl text-3xl font-semibold tracking-tight lg:text-4xl">
                O DCF precisa ser uma área operacional, não uma home vazia.
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 lg:text-base">
                Este painel concentra o que realmente importa: cobertura do módulo, lacunas de estrutura, carga de vinculações e os próximos movimentos para manter cargos e competências consistentes.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {quickActions.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.title}
                    type="button"
                    onClick={item.action}
                    className={`group rounded-2xl px-4 py-4 text-left transition-transform hover:-translate-y-0.5 ${item.className}`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className="h-5 w-5" />
                      <ArrowRight className="h-4 w-4 opacity-70 transition-transform group-hover:translate-x-1" />
                    </div>
                    <p className="mt-5 text-base font-semibold">{item.title}</p>
                    <p className="mt-1 text-sm opacity-85">{item.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/10 p-5 backdrop-blur">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-200">Situação do módulo</p>
                <p className="mt-1 text-3xl font-semibold">{dashboardData.stats.taxaCobertura}%</p>
              </div>
              <button
                type="button"
                onClick={loadDashboardData}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/15"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
            </div>

            <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-emerald-400"
                style={{ width: `${Math.min(100, dashboardData.stats.taxaCobertura || 0)}%` }}
              />
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-100">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Estruturas cadastradas</span>
                <strong>{dashboardData.stats.totalCargos}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Competências ativas</span>
                <strong>{dashboardData.insights.totalCompetencias}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Vinculações ativas</span>
                <strong>{dashboardData.insights.vinculacoesAtivas}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-amber-300/20 px-4 py-3 text-amber-100">
                <span>Lacunas operacionais</span>
                <strong>{dashboardData.insights.totalLacunas}</strong>
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-2xl border border-rose-300/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                {error}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Cargos estruturados</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{dashboardData.stats.totalCargos}</p>
            </div>
            <div className="rounded-2xl bg-slate-100 p-3">
              <Briefcase className="h-5 w-5 text-slate-700" />
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {dashboardData.stats.cargosComDCF} com status ativo e prontos para uso operacional.
          </p>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Banco de competências</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{dashboardData.insights.totalCompetencias}</p>
            </div>
            <div className="rounded-2xl bg-emerald-50 p-3">
              <Target className="h-5 w-5 text-emerald-700" />
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {dashboardData.insights.competenciasOciosas} ainda não vinculadas a nenhum cargo.
          </p>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Colaboradores vinculados</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{dashboardData.insights.vinculacoesAtivas}</p>
            </div>
            <div className="rounded-2xl bg-sky-50 p-3">
              <Users className="h-5 w-5 text-sky-700" />
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {dashboardData.stats.funcionariosSemDCF} ainda precisam de vínculo formal no módulo.
          </p>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Itens para ação</p>
              <p className="mt-2 text-3xl font-semibold text-slate-900">{dashboardData.insights.totalLacunas}</p>
            </div>
            <div className="rounded-2xl bg-amber-50 p-3">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            Soma das pendências de vínculo, estrutura e uso do banco de competências.
          </p>
        </article>
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-[1.4fr_1fr]">
        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Fila operacional</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">Prioridades do DCF</h2>
              <p className="mt-2 text-sm text-slate-600">
                Itens que hoje impactam cobertura, aderência do cargo e uso correto do banco de competências.
              </p>
            </div>
            <div className="rounded-2xl bg-slate-100 p-3">
              <Layers3 className="h-5 w-5 text-slate-700" />
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {priorityItems.map((item) => (
              <div key={item.title} className={`rounded-2xl border p-4 ${item.tone}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">{item.title}</p>
                  <span className="text-2xl font-semibold">{item.value}</span>
                </div>
                <p className="mt-2 text-sm opacity-90">{item.description}</p>
                <button
                  type="button"
                  onClick={item.action}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium"
                >
                  {item.actionLabel}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Distribuição</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">Cargos por departamento</h2>
            </div>
            <div className="rounded-2xl bg-slate-100 p-3">
              <Building2 className="h-5 w-5 text-slate-700" />
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {dashboardData.insights.departamentos.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Nenhum departamento encontrado nos cargos cadastrados.
              </p>
            ) : (
              dashboardData.insights.departamentos.map((item) => (
                <div key={item.nome}>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{item.nome}</span>
                    <span className="text-slate-500">{item.quantidade}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-slate-900"
                      style={{ width: `${(item.quantidade / maxDepartamento) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </article>
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Competências</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">Mais usadas</h2>
            </div>
            <div className="rounded-2xl bg-emerald-50 p-3">
              <Target className="h-5 w-5 text-emerald-700" />
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {dashboardData.insights.topCompetencias.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Nenhuma competência foi associada aos cargos ainda.
              </p>
            ) : (
              dashboardData.insights.topCompetencias.map((item, index) => (
                <div key={`${item.nome}-${index}`} className="flex items-center justify-between rounded-2xl border border-slate-100 px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{item.nome}</p>
                    <p className="text-sm text-slate-500">Presente em cargos do DCF</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                    {item.quantidade}
                  </span>
                </div>
              ))
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Atualizações</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">Últimos cargos mexidos</h2>
            </div>
            <div className="rounded-2xl bg-sky-50 p-3">
              <ClipboardCheck className="h-5 w-5 text-sky-700" />
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {dashboardData.insights.cargosRecentes.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Nenhum cargo foi encontrado no módulo.
              </p>
            ) : (
              dashboardData.insights.cargosRecentes.map((cargo) => (
                <div key={cargo.id} className="rounded-2xl border border-slate-100 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{cargo.nome_cargo}</p>
                      <p className="truncate text-sm text-slate-500">
                        {cargo.departamento || 'Sem departamento'} · {formatNivel(cargo.nivel_hierarquico)}
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {cargo.competencias_total} comp.
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    Atualizado em {formatDateTime(cargo.updated_at || cargo.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Adoção</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">Cargos mais vinculados</h2>
            </div>
            <div className="rounded-2xl bg-amber-50 p-3">
              <Users className="h-5 w-5 text-amber-700" />
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {dashboardData.insights.topCargos.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Nenhum cargo com vínculo encontrado.
              </p>
            ) : (
              dashboardData.insights.topCargos.map((cargo) => (
                <button
                  key={cargo.id}
                  type="button"
                  onClick={() => navigate('/dcf/cargos')}
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-100 px-4 py-3 text-left hover:border-slate-200 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">{cargo.nome_cargo}</p>
                    <p className="truncate text-sm text-slate-500">{cargo.departamento || 'Sem departamento'}</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                    {cargo.vinculados}
                  </span>
                </button>
              ))
            )}
          </div>
        </article>
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <button
          type="button"
          onClick={() => navigate('/dcf/cargos')}
          className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-transform hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between">
            <div className="rounded-2xl bg-slate-100 p-3">
              <Briefcase className="h-5 w-5 text-slate-700" />
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-slate-900">Gerenciar cargos</h3>
          <p className="mt-2 text-sm text-slate-600">
            Revisar DCFs, completar competências, editar estrutura e emitir documentos.
          </p>
        </button>

        <button
          type="button"
          onClick={() => navigate('/dcf/templates')}
          className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-transform hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between">
            <div className="rounded-2xl bg-violet-50 p-3">
              <BookOpen className="h-5 w-5 text-violet-700" />
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-slate-900">Templates com IA</h3>
          <p className="mt-2 text-sm text-slate-600">
            Acelerar criação de estrutura base quando o cargo ainda não existe no módulo.
          </p>
        </button>

        <button
          type="button"
          onClick={() => navigate('/dcf/relatorios')}
          className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-transform hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between">
            <div className="rounded-2xl bg-emerald-50 p-3">
              <BarChart3 className="h-5 w-5 text-emerald-700" />
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-slate-900">Relatórios e análises</h3>
          <p className="mt-2 text-sm text-slate-600">
            Explorar compliance, gaps, progressão de carreira e mapa estrutural do DCF.
          </p>
        </button>
      </section>

      <section className="rounded-3xl border border-emerald-200 bg-emerald-50 px-6 py-5 text-emerald-950 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="font-semibold">Próximo passo recomendado</p>
              <p className="mt-1 text-sm text-emerald-900">
                Se a fila operacional estiver alta, comece por vínculos pendentes e cargos sem competência. Isso limpa o maior volume de inconsistências com menos retrabalho.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dcf/vinculacoes')}
            className="inline-flex items-center gap-2 self-start rounded-full bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800"
          >
            Ir para vinculações
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default DCFDashboard;
